// src/store/redux/slices/MailerSlice.ts

import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppState } from '../types'
import { MailerAPI } from '../../../api/MailerAPI'
import { WritableDraft } from 'immer'
import { getRegionCode } from '../utils/getRegionCode'
import { PURGE } from 'redux-persist/es/constants'

export interface MailerState {
  isLoading: boolean
  error: string | null
}

export const initialState: MailerState = {
  isLoading: false,
  error: null,
}

interface SendMailPayload {
  subject: string
  to: number
  body: string
}

/**
 * Thunk to send an email via the MailerAPI
 */
export const sendMail = createAsyncThunk<string, SendMailPayload>(
  'mailer/sendMail',
  async (payload: SendMailPayload, { getState, rejectWithValue }) => {
    const { subject, to, body } = payload
    const regionCode = getRegionCode(getState() as AppState)
    const mailerAPI: MailerAPI = new MailerAPI(regionCode)
    try {
      return await mailerAPI.postMail(subject, to, body)
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

const mailerSlice = createSlice({
  name: 'mailer',
  initialState,
  reducers: {
    clearError: (state: WritableDraft<MailerState>) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(PURGE, () => initialState)
      .addCase(sendMail.pending, (state: WritableDraft<MailerState>) => {
        state.isLoading = true
        state.error = null // Clear previous errors
      })
      .addCase(sendMail.fulfilled, (state: WritableDraft<MailerState>, action: PayloadAction<string>) => {
        state.isLoading = false
        state.error = null
      })
      .addCase(sendMail.rejected, (state: WritableDraft<MailerState>, action) => {
        state.error = (action.error.message as string) || 'Failed to send email'
        state.isLoading = false
      })
  },
})

export const { clearError } = mailerSlice.actions
export default mailerSlice.reducer
